import type { ModalProps } from 'antd';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useResizableModal } from './useResizableModal';
import { ModalResizeHandles } from '@/components/ui/ModalResizeHandles';

interface UseResizableAntdModalOptions {
  /** kebab-case, versioned localStorage key, e.g. 'template-selection-modal-size-v1' */
  storageKey: string;
  defaultWidth: number;
  /** The scrollable BODY height (header/footer stay fixed). */
  defaultHeight: number;
  minWidth?: number;
  minHeight?: number;
}

/**
 * Returns antd <Modal> props that make it drag-resizable (corners + side/bottom
 * edges) and fullscreen-toggleable, with size persisted to localStorage. Spread
 * the result onto an antd Modal:
 *
 *   const resizable = useResizableAntdModal({ storageKey, defaultWidth, defaultHeight });
 *   <Modal open={open} title={...} footer={...} onCancel={...} {...resizable}>...</Modal>
 *
 * `defaultHeight` controls the scrollable body height; the antd header (title +
 * close X) and footer stay fixed. Top resize handles are intentionally omitted
 * so they don't cover the antd close button. The modal must be center-anchored
 * (this hook sets `centered`) for the drag math to track the cursor.
 */
export function useResizableAntdModal({
  storageKey,
  defaultWidth,
  defaultHeight,
  minWidth = 420,
  minHeight = 240,
}: UseResizableAntdModalOptions): Partial<ModalProps> {
  const rm = useResizableModal({ storageKey, defaultWidth, defaultHeight, minWidth, minHeight });

  return {
    centered: true,
    width: rm.isFullscreen ? 'calc(100vw - 32px)' : rm.width,
    styles: {
      body: {
        height: rm.isFullscreen ? 'calc(100vh - 200px)' : rm.height,
        overflowY: 'auto',
      },
    },
    modalRender: (node) => (
      <div className="relative">
        {node}
        <button
          type="button"
          onClick={rm.toggleFullscreen}
          className="absolute right-11 top-3.5 z-[5] rounded-sm p-0.5 text-muted-foreground opacity-70 transition-opacity hover:text-coral hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-coral/30"
          aria-label={rm.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          title={rm.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {rm.isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        {/* No top handles — the top edge holds the antd header + close button. */}
        {!rm.isFullscreen && (
          <ModalResizeHandles onStart={rm.startResize} dirs={['s', 'e', 'w', 'se', 'sw']} />
        )}
      </div>
    ),
  };
}
